import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { assignmentBlock } from '@/lib/friendlyCodes';
import { RefreshCw } from 'lucide-react';
import {
  format, addDays, startOfWeek, endOfWeek, addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths,
} from 'date-fns';

import { CalendarToolbar } from '@/components/calendar/CalendarToolbar';
import { CalendarFilters } from '@/components/calendar/CalendarFilters';
import { isHiddenCancelled, readHideCancelled, saveHideCancelled, isOpenRosterShift } from '@/components/calendar/cancelledShiftRules';   // BS3-003
import { CalendarWeekView } from '@/components/calendar/CalendarWeekView';
import { CalendarDayView } from '@/components/calendar/CalendarDayView';
import { CalendarMonthView } from '@/components/calendar/CalendarMonthView';
import { CalendarTimelineView } from '@/components/calendar/CalendarTimelineView';
import { CalendarShiftDrawer } from '@/components/calendar/CalendarShiftDrawer';
import { CalendarGroupSheet } from '@/components/calendar/CalendarGroupSheet';   // UC-B-2
import { CalendarSILHouseDrawer } from '@/components/calendar/CalendarSILHouseDrawer';
import { CalendarCentreDrawer } from '@/components/calendar/CalendarCentreDrawer';
import { CalendarPublishDialog } from '@/components/calendar/CalendarPublishDialog';
import { CalendarCreateShiftModal } from '@/components/calendar/CalendarCreateShiftModal';
import { getShiftPeriod } from '@/components/calendar/ShiftPeriodBadge';
import { staffHoursFromShifts } from '@/components/calendar/calendarUtils';   // client#13
import { StaffHoursSummary } from '@/components/calendar/StaffHoursSummary';

const ROW_PAGE = 20;
const EMPTY_BOARD = { rows: [], totalRows: 0, hasMore: false, summary: null, publish: null, dailyTotals: {}, openShifts: [], staffHours: [] };

// CalendarV3 — Scheduling Board.
// Week/Timeline rows are served pre-aggregated and paginated by GET /roster/board ("Load more" = next
// backend page). Day/Month views fetch the shifts for their own range. Reference lists (participants,
// staff, services) are loaded once for the pickers.
export default function CalendarV3() {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const [board, setBoard] = useState(EMPTY_BOARD);
  const [rangeShifts, setRangeShifts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // day | week | month | timeline
  const [groupBy, setGroupBy] = useState('participant'); // participant | staff | sil | centre
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState([]);
  const [openRoster, setOpenRoster] = useState(false);
  // BS3-003: hide cancelled shifts that will not be invoiced — ON by default, remembered per browser
  const [hideCancelled, setHideCancelledState] = useState(readHideCancelled);
  const setHideCancelled = useCallback((on) => { setHideCancelledState(on); saveHideCancelled(on); }, []);
  const [branchFilter, setBranchFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedShift, setSelectedShift] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);   // UC-B-2: group sheet is a sibling of the shift sheet (no route)
  const [showFilters, setShowFilters] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState({ date: '', staffId: '', participantId: '' });
  const [silDrawer, setSilDrawer] = useState({ open: false, houseId: null, shifts: [] });
  const [centreDrawer, setCentreDrawer] = useState({ open: false, sessionId: null, shifts: [] });
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishSubset, setPublishSubset] = useState(null);   // UC-D (UI-24): Publish from the house day view = existing Publish action over that house's shifts
  // Day 22 deep links from Readiness Centre / Billing Readiness: ?shift=<id> opens that shift's drawer, ?participant=<id> pre-filters.
  const [searchParams] = useSearchParams();
  const deepShiftRef = useRef(searchParams.get('shift'));
  useEffect(() => {
    const pid = searchParams.get('participant');
    if (!pid) return;
    // Day 23 (testing agent P2): the board search is name-based — resolve the id to the participant's name instead of showing a raw id
    api.get(`/participants/${pid}`).then((r) => {
      const p = r.data?.data || r.data || {};
      const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
      setSearchTerm(name || pid);
    }).catch(() => setSearchTerm(pid));
  }, [searchParams]);
  useEffect(() => {
    const sid = deepShiftRef.current;
    if (!sid) return;
    deepShiftRef.current = null;
    api.get(`/roster/shifts/${sid}`).then((r) => {
      const sh = r.data?.data || r.data;
      if (!sh?.id) return;
      if (sh.date) setCurrentDate(new Date(`${sh.date}T00:00:00`));
      setSelectedShift(sh); setDrawerOpen(true);
    }).catch(() => toast.error('Shift not found or not in your scope'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isBoardView = viewMode === 'week' || viewMode === 'timeline';

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Compute date range based on view
  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const dateRange = useMemo(() => {
    if (viewMode === 'day') return { start: format(currentDate, 'yyyy-MM-dd'), end: format(currentDate, 'yyyy-MM-dd') };
    if (viewMode === 'month') {
      const ms = startOfMonth(currentDate);
      const me = endOfMonth(currentDate);
      return { start: format(startOfWeek(ms, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(me, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
    }
    return { start: format(days[0], 'yyyy-MM-dd'), end: format(days[6], 'yyyy-MM-dd') };
  }, [viewMode, currentDate, days]);

  const boardParams = useMemo(() => ({
    start_date: dateRange.start, end_date: dateRange.end, group_by: groupBy,
    search: debouncedSearch || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    shift_period: periodFilter.length ? periodFilter.join(',') : undefined,
    view: openRoster ? 'open_roster' : undefined,
    branch_id: branchFilter || undefined,
    hide_cancelled: hideCancelled && statusFilter !== 'CANCELLED' ? true : undefined,   // BS3-003
  }), [dateRange, groupBy, debouncedSearch, statusFilter, periodFilter, openRoster, branchFilter, hideCancelled]);

  // Reference lists for pickers/drawers — loaded once.
  useEffect(() => {
    Promise.all([
      fetchAllPages('/participants').catch(() => []),
      fetchAllPages('/staff').catch(() => []),
      api.get('/services/catalog', { params: { mode: 'selection' } }).catch(() => ({ data: {} })),
      api.get('/org/branches').catch(() => ({ data: {} })),
    ]).then(([p, s, sv, br]) => {
      setParticipants(p);
      setStaff(s);
      setServices(sv.data?.items || sv.data?.data || []);
      setBranches((br.data?.items || br.data?.data || []).map(b => ({ id: b.id || b._id, name: b.name })));
    });
  }, []);

  const reqId = useRef(0);
  const fetchBoard = useCallback(async ({ page = 1, pageSize = ROW_PAGE, append = false } = {}) => {
    const id = ++reqId.current;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await api.get('/roster/board', { params: { ...boardParams, page, pageSize } });
      if (id !== reqId.current) return;
      const d = res.data;
      setBoard(prev => {
        const seen = new Set(append ? prev.rows.map(r => r.id) : []);
        const rows = append ? [...prev.rows, ...d.rows.filter(r => !seen.has(r.id))] : d.rows;
        return { rows, totalRows: d.total_rows, hasMore: d.has_more, summary: d.summary, publish: d.publish, dailyTotals: d.daily_totals, openShifts: d.open_shifts, staffHours: d.staff_hours || [],
          hiddenCancelled: d.hidden_cancelled || 0 };
      });
    } catch { toast.error('Failed to load schedule'); }
    finally { if (id === reqId.current) { setLoading(false); setLoadingMore(false); } }
  }, [boardParams]);

  const fetchRangeShifts = useCallback(async () => {
    setLoading(true);
    try { setRangeShifts(await fetchAllPages('/roster/shifts', { start_date: dateRange.start, end_date: dateRange.end })); }
    catch { toast.error('Failed to load shifts'); }
    finally { setLoading(false); }
  }, [dateRange]);

  useEffect(() => { if (isBoardView) fetchBoard(); }, [isBoardView, fetchBoard]);
  useEffect(() => { if (!isBoardView) fetchRangeShifts(); }, [isBoardView, fetchRangeShifts]);

  const handleLoadMore = () => {
    if (!board.hasMore || loadingMore) return;
    fetchBoard({ page: Math.floor(board.rows.length / ROW_PAGE) + 1, append: true });
  };

  // CR-03-11 #6: every participant's roster at once (the board API returns up to 200 rows per request).
  const handleShowAll = () => {
    if (!board.hasMore || loadingMore) return;
    fetchBoard({ page: 1, pageSize: Math.min(200, board.totalRows) });
  };

  // Re-fetch after a mutation, keeping every row the user has already paged in.
  const refresh = useCallback(() => {
    if (isBoardView) fetchBoard({ page: 1, pageSize: Math.min(200, Math.max(ROW_PAGE, board.rows.length)) });
    else fetchRangeShifts();
  }, [isBoardView, fetchBoard, fetchRangeShifts, board.rows.length]);

  // Day / Month views filter client-side over their own range.
  const filteredRangeShifts = useMemo(() => {
    let r = rangeShifts;
    if (statusFilter !== 'all') r = r.filter(s => s.status === statusFilter);
    if (hideCancelled && statusFilter !== 'CANCELLED') r = r.filter(s => !isHiddenCancelled(s));   // BS3-003
    if (openRoster) r = r.filter(isOpenRosterShift);   // Day / Month: same Open Roster rule as the board API
    if (periodFilter.length > 0) {
      const set = new Set(periodFilter);
      r = r.filter(s => set.has(getShiftPeriod(s).code));
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      // client#12: same rule as the board API — an open shift shows only when it matches the search itself
      const hay = (s) => [s.participant_name, s.service_name, s.house_name, ...(s.linked_participant_details || []).map(p => p.name), ...(s.assigned_staff || []).map(st => st.name)];
      r = r.filter(s => hay(s).some(h => (h || '').toLowerCase().includes(q)));
    }
    return r;
  }, [rangeShifts, statusFilter, periodFilter, debouncedSearch, hideCancelled, openRoster]);

  // Every shift currently known to the client (for keeping the open drawer in sync after refetch).
  const knownShifts = useMemo(
    () => (isBoardView ? [...board.openShifts, ...board.rows.flatMap(r => r.shifts || [])] : rangeShifts),
    [isBoardView, board.openShifts, board.rows, rangeShifts],
  );
  useEffect(() => {
    if (!selectedShift) return;
    const fresh = knownShifts.find(s => s.id === selectedShift.id);
    if (fresh && fresh !== selectedShift) setSelectedShift(fresh);
  }, [knownShifts, selectedShift]);

  // Navigation
  const goNext = () => {
    if (viewMode === 'day') setCurrentDate(d => addDays(d, 1));
    else if (viewMode === 'month') setCurrentDate(d => addMonths(d, 1));
    else setCurrentDate(d => addWeeks(d, 1));
  };
  const goPrev = () => {
    if (viewMode === 'day') setCurrentDate(d => addDays(d, -1));
    else if (viewMode === 'month') setCurrentDate(d => subMonths(d, 1));
    else setCurrentDate(d => subWeeks(d, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  // UC-B: a card's group chip passes { openGroup: true } → group sheet; otherwise the shift sheet.
  const handleShiftClick = (shift, opts) => {
    setSelectedShift(shift);
    if (opts?.openGroup && shift?.shared_group_id) { setDrawerOpen(false); setGroupSheetOpen(true); } else { setGroupSheetOpen(false); setDrawerOpen(true); }
  };
  // `row` is the grid row that was clicked (staff / participant / sil / centre) or null from Month view.
  const handleCellClick = (row, dateStr) => {
    setCreatePrefill({
      date: dateStr,
      staffId: row?.kind === 'staff' ? row.id : '',
      participantId: row?.kind === 'participant' && row.id !== 'unassigned' ? row.id : '',
    });
    setCreateOpen(true);
  };
  const handleRowClick = (row) => {
    if (row.kind === 'sil') setSilDrawer({ open: true, houseId: row.id, shifts: row.shifts || [] });
    else if (row.kind === 'centre') setCentreDrawer({ open: true, sessionId: row.id, shifts: row.shifts || [] });
  };

  // Drag & drop reschedule
  const handleDragDrop = async (shiftId, newDate) => {
    try {
      await api.put(`/roster/shifts/${shiftId}`, { shift_date: newDate });
      toast.success(`Shift moved to ${newDate}`);
      refresh();
    } catch (e) {
      const blk = assignmentBlock(e);
      if (blk) toast.error(`Cannot move shift — ${blk.title}`, { description: blk.reasons.join(' · ') || undefined });
      else toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to move shift');
    }
  };

  // Date display
  const dateLabel = viewMode === 'day' ? format(currentDate, 'EEEE, d MMMM yyyy')
    : viewMode === 'month' ? format(currentDate, 'MMMM yyyy')
    : `${format(days[0], 'd MMM')} – ${format(days[6], 'd MMM yyyy')}`;

  // Publish status — range-wide from the board payload; computed client-side for Day/Month.
  const publishStats = useMemo(() => {
    if (isBoardView) return { unpublishedCount: board.publish?.unpublished_count || 0, lastPublishedAt: board.publish?.last_published_at || null };
    let unpublished = 0;
    let lastPublishedAt = null;
    for (const s of filteredRangeShifts) {
      if (s.status === 'CANCELLED') continue;
      const pub = s.published_at;
      const upd = s.updated_at;
      if (!pub || (upd && new Date(upd) > new Date(pub))) unpublished += 1;
      if (pub && (!lastPublishedAt || new Date(pub) > new Date(lastPublishedAt))) lastPublishedAt = pub;
    }
    return { unpublishedCount: unpublished, lastPublishedAt };
  }, [isBoardView, board.publish, filteredRangeShifts]);

  const showSpinner = loading && (isBoardView ? board.rows.length === 0 && board.openShifts.length === 0 : rangeShifts.length === 0);
  // client#13: a searched worker's scheduled hours over the visible date range (board: whole range from the API;
  // day / month: from the shifts already loaded for that range)
  const searchedStaffHours = !debouncedSearch ? [] : (isBoardView ? (board.staffHours || []) : staffHoursFromShifts(filteredRangeShifts, debouncedSearch));

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="calendar-v3">
      <CalendarToolbar
        viewMode={viewMode} setViewMode={setViewMode}
        filteredShifts={filteredRangeShifts} summary={isBoardView ? board.summary : null} dateLabel={dateLabel}
        onPrev={goPrev} onToday={goToday} onNext={goNext}
        onCreateShift={() => { setCreatePrefill({ date: format(currentDate, 'yyyy-MM-dd'), staffId: '', participantId: '' }); setCreateOpen(true); }}
        onPublish={() => setPublishOpen(true)}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onRefresh={refresh}
        unpublishedCount={publishStats.unpublishedCount}
        lastPublishedAt={publishStats.lastPublishedAt}
      />

      <div className="flex-1 flex overflow-hidden">
        {showFilters && (
          <CalendarFilters
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            hideCancelled={hideCancelled} setHideCancelled={setHideCancelled}
            hiddenCancelledCount={hideCancelled && statusFilter !== 'CANCELLED'
              ? (isBoardView ? board.hiddenCancelled || 0 : rangeShifts.filter(isHiddenCancelled).length) : 0}
            periodFilter={periodFilter} setPeriodFilter={setPeriodFilter}
            groupBy={groupBy} setGroupBy={setGroupBy}
          
            openRoster={openRoster} setOpenRoster={setOpenRoster} branchFilter={branchFilter} setBranchFilter={setBranchFilter} branches={branches} />
        )}

        <div className={`flex-1 overflow-auto bg-white transition-opacity ${loading && !showSpinner ? 'opacity-60' : ''}`} data-testid="calendar-grid" aria-busy={loading}>
          <StaffHoursSummary items={searchedStaffHours} rangeLabel={dateLabel} />
          {showSpinner ? (
            <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : viewMode === 'day' ? (
            <CalendarDayView date={currentDate} filteredShifts={filteredRangeShifts} onShiftClick={handleShiftClick} />
          ) : viewMode === 'month' ? (
            <CalendarMonthView date={currentDate} filteredShifts={filteredRangeShifts} onShiftClick={handleShiftClick} onCellClick={handleCellClick} />
          ) : viewMode === 'timeline' ? (
            <CalendarTimelineView days={days} rows={board.rows} groupBy={groupBy} onShiftClick={handleShiftClick}
              totalRows={board.totalRows} hasMore={board.hasMore} onLoadMore={handleLoadMore} onShowAll={handleShowAll} loadingMore={loadingMore} />
          ) : (
            <CalendarWeekView days={days} rows={board.rows} openShifts={board.openShifts} dailyTotals={board.dailyTotals}
              totalRows={board.totalRows} hasMore={board.hasMore} onLoadMore={handleLoadMore} onShowAll={handleShowAll} loadingMore={loadingMore}
              groupBy={groupBy} searchTerm={debouncedSearch}
              onShiftClick={handleShiftClick} onCellClick={handleCellClick}
              onDragDrop={handleDragDrop} onRowClick={handleRowClick} permissions={permissions} />
          )}
        </div>
      </div>

      <CalendarShiftDrawer shift={selectedShift} open={drawerOpen} onClose={() => setDrawerOpen(false)} staff={staff} onUpdate={refresh} permissions={permissions}
        participants={participants} services={services}
        onShowHouseView={(shift) => {
          setGroupBy('sil'); setDrawerOpen(false);
          // open this shift's house day view directly when the shift names its house
          const houseId = shift?.house_id || shift?.linked_house_id;
          if (houseId) setSilDrawer({ open: true, houseId, date: shift.date, shifts: knownShifts.filter((s) => (s.house_id || s.linked_house_id) === houseId) });
        }}
        onOpenGroup={(shift) => handleShiftClick(shift, { openGroup: true })} />
      <CalendarGroupSheet shift={selectedShift} open={groupSheetOpen} onClose={() => setGroupSheetOpen(false)} staff={staff} onUpdate={refresh}
        onOpenShift={(member) => {
          const known = knownShifts.find((s) => s.id === member.id);
          if (known) return handleShiftClick(known);
          return api.get(`/roster/shifts/${member.id}`).then((r) => handleShiftClick(r.data?.data || r.data || member)).catch(() => handleShiftClick(member));
        }} />

      <CalendarSILHouseDrawer
        houseId={silDrawer.houseId}
        allShifts={silDrawer.shifts}
        initialDate={silDrawer.date}
        open={silDrawer.open}
        onClose={() => setSilDrawer({ open: false, houseId: null, shifts: [] })}
        onShiftClick={(s) => { setSilDrawer({ open: false, houseId: null, shifts: [] }); handleShiftClick(s); }}
        staff={staff} onChanged={refresh}
        onOpenGroup={(gid) => { setSilDrawer({ open: false, houseId: null, shifts: [] }); api.get(`/shared-delivery/groups/${gid}`).then((r) => { const m = (r.data?.data?.members || [])[0]; handleShiftClick({ ...(m || { id: `group:${gid}` }), shared_group_id: gid }, { openGroup: true }); }).catch(() => {}); }}
        onPublish={(houseShifts) => { setPublishSubset(houseShifts); setPublishOpen(true); }}
      />

      <CalendarCentreDrawer
        sessionId={centreDrawer.sessionId}
        allShifts={centreDrawer.shifts}
        open={centreDrawer.open}
        onClose={() => setCentreDrawer({ open: false, sessionId: null, shifts: [] })}
        onShiftClick={(s) => { setCentreDrawer({ open: false, sessionId: null, shifts: [] }); handleShiftClick(s); }}
      />

      <CalendarPublishDialog
        open={publishOpen}
        onClose={() => { setPublishOpen(false); setPublishSubset(null); }}
        dateRange={dateRange}
        shifts={publishSubset || filteredRangeShifts}
        stats={isBoardView ? board.publish : null}
        onPublished={refresh}
      />

      <CalendarCreateShiftModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={refresh}
        prefillDate={createPrefill.date} prefillStaffId={createPrefill.staffId} prefillParticipantId={createPrefill.participantId}
        participants={participants} services={services} staff={staff} />
    </div>
  );
}
